import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TYPE_LABELS = {
  MENTORSHIP_UP: 'Mentorship',
  MENTORSHIP_HIGH: 'Mentorship',
  PEER_MID_LEVEL: 'Peer Networking',
  PEER_SENIOR: 'Professional Networking',
  BOARD_PATHWAY: 'Board Advisory',
  HBCU_MENTORSHIP: 'HBCU Mentorship',
};

const ConsentResponse = () => {
  const { token } = useParams();
  const [consentInfo, setConsentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responseStatus, setResponseStatus] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);

  useEffect(() => {
    const fetchConsent = async () => {
      try {
        const res = await fetch(`${API_URL}/api/brokerage/consent/${token}`);
        if (res.status === 404) {
          setError('This consent request was not found. The link may be invalid.');
          return;
        }
        if (res.status === 410) {
          setError('This consent request has expired.');
          return;
        }
        if (!res.ok) {
          setError('Something went wrong. Please try again later.');
          return;
        }
        const data = await res.json();
        if (data.already_responded) {
          setSubmitted(true);
          setResponseStatus(data.consent_status);
        }
        setConsentInfo(data);
      } catch {
        setError('Unable to load consent request. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchConsent();
  }, [token]);

  const handleRespond = async (status) => {
    setResponding(true);
    try {
      const res = await fetch(`${API_URL}/api/brokerage/consent/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          declined_reason: status === 'declined' ? declineReason : null,
        }),
      });
      if (res.status === 409) {
        setSubmitted(true);
        setResponseStatus(status);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Failed to submit your response. Please try again.');
        return;
      }
      setSubmitted(true);
      setResponseStatus(status);
    } catch {
      setError('Unable to submit your response. Please check your connection and try again.');
    } finally {
      setResponding(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading consent request...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !consentInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <img
            src="https://scholars-uploads.s3.us-east-2.amazonaws.com/headshots/nacme_logo.jpg"
            alt="NACME"
            className="h-12 mx-auto mb-6"
          />
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Request</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Already responded
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <img
            src="https://scholars-uploads.s3.us-east-2.amazonaws.com/headshots/nacme_logo.jpg"
            alt="NACME"
            className="h-12 mx-auto mb-6"
          />
          {responseStatus === 'granted' ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h1>
              <p className="text-gray-600 mb-4">
                Your consent has been recorded. A NACME staff member will be in touch to coordinate the introduction.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">Response Recorded</h1>
              <p className="text-gray-600 mb-4">
                Thank you for letting us know. We respect your decision and appreciate your time.
              </p>
            </>
          )}
          <p className="text-sm text-gray-400">You may close this page.</p>
        </div>
      </div>
    );
  }

  // Consent form
  const requestType = TYPE_LABELS[consentInfo.request_type] || 'Introduction';
  const expiresDate = consentInfo.expires_at
    ? new Date(consentInfo.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-indigo-700 px-6 py-5 text-center">
          <img
            src="https://scholars-uploads.s3.us-east-2.amazonaws.com/headshots/nacme_logo.jpg"
            alt="NACME"
            className="h-10 mx-auto mb-3 rounded"
          />
          <h1 className="text-xl font-bold text-white">Consent Request</h1>
          <p className="text-indigo-200 text-sm mt-1">{requestType} Introduction</p>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <p className="text-gray-700">
            A NACME professional has expressed interest in connecting with you. Before we facilitate an introduction,
            we need your consent.
          </p>

          {/* Requester teaser */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-amber-800 uppercase mb-2">About the Requester</p>
            <p className="text-gray-800">{consentInfo.teaser_shown}</p>
          </div>

          {/* Privacy assurance */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-blue-800 uppercase mb-2">Your Privacy</p>
            <p className="text-sm text-blue-900">
              Your identity has <strong>not</strong> been disclosed to the requester and will not be shared
              unless you grant consent below.
            </p>
          </div>

          {/* Professional conduct disclaimer */}
          <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-700 uppercase mb-2">Professional Conduct Agreement</p>
            <p className="text-xs text-gray-700 leading-relaxed">
              The meeting requester has agreed to abide by{' '}
              <a
                href="/professional-conduct"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 underline hover:text-indigo-800"
              >
                these standards of professional conduct
              </a>{' '}
              and asserts that this inquiry will be career-related. NACME makes no assurances about the nature
              and substance of the inquiry, other than that the discovery of inappropriate or unprofessional
              conduct will result in removal from our program.
            </p>
            <p className="text-xs text-gray-700 leading-relaxed mt-2">
              By consenting to connect, you assert that you will also abide by{' '}
              <a
                href="/professional-conduct"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 underline hover:text-indigo-800"
              >
                these standards of professional conduct
              </a>{' '}
              and that your responses and interaction will be governed by these standards. You also agree to
              make a sincere effort to answer legitimate inquiries about career development and professional growth.
            </p>
          </div>

          {expiresDate && (
            <p className="text-sm text-gray-500 text-center">
              This request expires on <strong>{expiresDate}</strong>
            </p>
          )}

          {/* Decline reason form */}
          {showDeclineForm && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Would you like to share a reason? (Optional)
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={3}
                className="w-full text-sm border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Your feedback helps us improve our matching..."
              />
              <div className="flex gap-3">
                <button
                  onClick={() => handleRespond('declined')}
                  disabled={responding}
                  className="flex-1 py-2.5 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {responding ? 'Submitting...' : 'Confirm Decline'}
                </button>
                <button
                  onClick={() => { setShowDeclineForm(false); setDeclineReason(''); }}
                  className="px-4 py-2.5 text-sm font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!showDeclineForm && (
            <div className="flex gap-3">
              <button
                onClick={() => handleRespond('granted')}
                disabled={responding}
                className="flex-1 py-3 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {responding ? 'Submitting...' : 'I Consent to This Introduction'}
              </button>
              <button
                onClick={() => setShowDeclineForm(true)}
                disabled={responding}
                className="px-5 py-3 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Decline
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            NACME Executive Relationship Program
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsentResponse;
